import { useEffect, useRef, useState } from "react";
import { DraggablePiechart } from "./DraggablePiechart";

export default function PieChart({
  items,
  dispatch,
  questionState,
  isExplanation = false,
  presetPercentages
}) {
  const containerRef = useRef(null);
  const pieRef = useRef(null);
  const dispatchTimerRef = useRef(null);
  const pointerDownRef = useRef(false);
  const minProportion = 0.05;

  const [canvasSize, setCanvasSize] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  const defaultColors = [
    "#FF6B6B",
    "#4ECDC4",
    "#BB8FCE",
    "#F8B500",
    "#FFA07A",
    "#85C1E2",
    "#F7DC6F",
    "#45B7D1",
    "#52B788",
    "#98D8C8",
  ];

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 🔸 Tentukan size per breakpoint
  useEffect(() => {
    let size = 240; // default XS/mobile
    if (windowWidth >= 600 && windowWidth < 960) size = 320; // SM–MD
    if (windowWidth >= 960) size = 400; // MD ke atas

    setCanvasSize(size);
  }, [windowWidth]);

  // ✅ simpan props terbaru supaya event listener window tidak pakai props versi lama
  const latestRef = useRef({
    dispatch,
    questionState,
    isExplanation,
    presetPercentages,
  });

  useEffect(() => {
    latestRef.current = { dispatch, questionState, isExplanation, presetPercentages };
  }, [dispatch, questionState, isExplanation, presetPercentages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (items.length === 0) return;

    // --- buat canvas dan append ke container ---
    const canvas = document.createElement("canvas");
    canvas.width = canvasSize; // resolution chart, harus sama dengan ukuran yang tampil
    canvas.height = canvasSize; // resolution chart, harus sama dengan ukuran yang tampil
    canvas.style.width = `${canvasSize}px`; // ukuran tampil
    canvas.style.height = `${canvasSize}px`; // ukuran tampil
    canvas.style.transition = "opacity 0.5s ease, transform 0.5s ease";
    canvas.id = "piechart";
    container.appendChild(canvas);

    const addPointerListeners = !isExplanation;

    if (addPointerListeners) {
      canvas.style.pointerEvents = "auto";
      canvas.style.cursor = "grab";
    } else {
      canvas.style.pointerEvents = "none";
      canvas.style.cursor = "default";
    }

    const onPointerDown = () => {
      pointerDownRef.current = true;
      if (dispatchTimerRef.current !== null) {
        clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }
      canvas.style.cursor = "grabbing";
    };

    const onPointerUp = () => {
      if (!pointerDownRef.current) return;
      pointerDownRef.current = false;

      // ✅ batalkan timer lama kalau ada (kalau sebelumnya pernah pakai delay)
      if (dispatchTimerRef.current !== null) {
        clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }

      canvas.style.cursor = "grab";

      try {
        const cur = pieRef.current
          ?.getAllSliceSizePercentages()
          ?.map((v) => Math.round(v));

        // ✅ ambil props terbaru dari ref (bukan dari closure lama)
        const { dispatch: d, questionState: qs, isExplanation: exp, presetPercentages: pp } =
          latestRef.current;

        // ✅ dispatch langsung biar tombol simpan nggak sempet ke-trigger pakai data lama
        if (cur && d && qs && !exp && !pp?.length) {
          d({
            type: "question/setQuestion",
            payload: {
              ...qs,
              answerText: cur.map(String),
            },
          });
        }
      } catch {
        // ignore
      }
    };

    const onPointerLeave = () => {
      if (pointerDownRef.current) canvas.style.cursor = "grab";
    };

    if (addPointerListeners) {
      canvas.addEventListener("pointerdown", onPointerDown);
      canvas.addEventListener("pointerleave", onPointerLeave);
      window.addEventListener("pointerup", onPointerUp);
    } else {
      canvas.style.cursor = "default";
    }

    // ------------------ LOGIC PROPORTION ------------------

    function generateRandomProportions(n, min) {
      const rnd = Array.from({ length: n }, () => Math.random());
      const total = rnd.reduce((a, v) => a + v, 0);
      const base = rnd.map((v) => v / total);

      function enforceMin(proportions) {
        let corrected = proportions.slice();
        let changed = true;
        while (changed) {
          changed = false;
          for (let i = 0; i < corrected.length; i++) {
            if (corrected[i] < min) {
              const diff = min - corrected[i];
              const maxVal = Math.max(...corrected);
              const maxIdx = corrected.indexOf(maxVal);
              if (maxIdx === i) continue;
              const take = Math.min(diff, corrected[maxIdx] - min);
              if (take > 0) {
                corrected[i] += take;
                corrected[maxIdx] -= take;
                changed = true;
              }
            }
          }
        }
        const t = corrected.reduce((a, v) => a + v, 0);
        return corrected.map((v) => v / t);
      }

      return enforceMin(base);
    }

    function normalizeToFractions(values, min, count) {
      const arr = values.slice(0, count);
      while (arr.length < count) arr.push(0);

      const sum = arr.reduce((a, v) => a + v, 0);
      const asFraction = sum > 1 ? arr.map((v) => v / 100) : arr; // kalau 0-100 → jadi 0-1

      const total = asFraction.reduce((a, v) => a + v, 0);
      const base =
        total > 0
          ? asFraction.map((v) => v / total)
          : Array(count).fill(1 / count);

      // enforce minimum slice
      let corrected = base.slice();
      let changed = true;
      while (changed) {
        changed = false;
        for (let i = 0; i < corrected.length; i++) {
          if (corrected[i] < min) {
            const diff = min - corrected[i];
            const maxVal = Math.max(...corrected);
            const maxIdx = corrected.indexOf(maxVal);
            if (maxIdx === i) continue;

            const take = Math.min(diff, corrected[maxIdx] - min);
            if (take > 0) {
              corrected[i] += take;
              corrected[maxIdx] -= take;
              changed = true;
            }
          }
        }
      }

      const t = corrected.reduce((a, v) => a + v, 0);
      return corrected.map((v) => v / t);
    }

    const randomProps = generateRandomProportions(items.length, minProportion);

    // ✅ Parse questionState.answerText (string array) back to numbers
    const parseAnswerText = (ans) => {
      if (!ans || !Array.isArray(ans)) return [];
      return ans.map((v) => {
        const parsed = typeof v === "string" ? parseFloat(v) : v;
        return isNaN(parsed) ? 0 : parsed;
      });
    };

    const answerTextNumbers = parseAnswerText(questionState?.answerText);

    const effectiveProps =
      isExplanation
        ? (presetPercentages?.length
          ? normalizeToFractions(presetPercentages, minProportion, items.length) // pembahasan
          : randomProps)
        : (answerTextNumbers.length > 0
          ? normalizeToFractions(answerTextNumbers, minProportion, items.length) // jawaban user (lanjutkan)
          : randomProps); // pertama kali

    const colorPalette = defaultColors;

    const proportions = items.map((label, i) => ({
      proportion: effectiveProps[i],
      format: {
        label,
        color: colorPalette[i % colorPalette.length],
        idx: i,
      },
    }));

    // ------------------ CUSTOM DRAW SEGMENT ------------------

    const drawSegment = function (
      context,
      _piechart,
      centerX,
      centerY,
      radius,
      startingAngle,
      arcSize,
      format,
      collapsed
    ) {
      if (collapsed) return;

      // slice
      context.save();
      const endingAngle = startingAngle + arcSize;
      context.beginPath();
      context.moveTo(centerX, centerY);
      context.arc(centerX, centerY, radius, startingAngle, endingAngle, false);
      context.closePath();
      context.fillStyle = format.color || "#f5f5f5";
      context.fill();
      context.stroke();
      context.restore();

      // hitung persentase sekali
      const percent = Math.round((arcSize / (2 * Math.PI)) * 100);

      // label di pinggir → sembunyikan kalau < 10%
      if (percent >= 10) {
        context.save();
        const fontSize = Math.floor(context.canvas.height / 25);
        const absVal = Math.abs(startingAngle) * 2;
        let dx, dy;

        context.translate(centerX, centerY);
        context.rotate(absVal > Math.PI ? Math.PI + startingAngle : startingAngle);
        context.textAlign = absVal > Math.PI ? "left" : "right";

        if (absVal > Math.PI) {
          dx = 0 - (radius - fontSize);
          dy = 5 - fontSize;
        } else {
          dx = radius - fontSize;
          dy = fontSize + 5;
        }

        const labelFontSize = Math.floor(fontSize * 0.7);
        context.font = `${labelFontSize}pt Helvetica`;
        context.fillText(format.label, dx, dy);
        context.restore();
      }

      // persentase di tengah slice (tetap tampil)
      context.save();
      const fontSize = Math.floor(context.canvas.height / 25);
      const midAngle = startingAngle + arcSize / 2;
      const percentRadius = radius * 0.7;
      const percentX = centerX + percentRadius * Math.cos(midAngle);
      const percentY = centerY + percentRadius * Math.sin(midAngle);

      context.font = `bold ${fontSize + 2}pt Helvetica`;
      context.fillStyle = "#333";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(`${percent}%`, percentX, percentY);
      context.restore();
    };

    const TAU = Math.PI * 2;
    const minAngle = minProportion * TAU;

    // ------------------ INIT DRAGGABLE PIE ------------------

    const pie = new DraggablePiechart({
      canvas,
      radius: 0.9,
      collapsing: false,
      minAngle: minAngle,
      proportions,
      drawSegment,
      onchange: () => {
        // onchange original kamu tidak melakukan apa-apa selain cek pointerDown,
        // jadi tetap dipertahankan
        if (!pointerDownRef.current) return;
      },
    });

    pieRef.current = pie;

    // animasi muncul
    canvas.style.opacity = "0";
    canvas.style.transform = "scale(0.5) rotate(-180deg)";
    canvas.style.transition =
      "opacity 0.8s ease, transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)";
    pie.radius = 0;
    pie.draw();

    setTimeout(() => {
      canvas.style.opacity = "1";
      canvas.style.transform = "scale(1) rotate(0deg)";
      if (!isExplanation) canvas.style.cursor = "grab";

      const originalRadius = 0.9;
      let startTime = null;
      const duration = 1200;

      function animateRadius(currentTime) {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        pie.radius = originalRadius * easeProgress;
        pie.draw();
        if (progress < 1) requestAnimationFrame(animateRadius);
      }

      requestAnimationFrame(animateRadius);
    }, 50);

    // cleanup
    return () => {
      if (addPointerListeners) {
        canvas.removeEventListener("pointerdown", onPointerDown);
        canvas.removeEventListener("pointerleave", onPointerLeave);
        window.removeEventListener("pointerup", onPointerUp);
      }

      // bersihin event internal DraggablePiechart
      pieRef.current?.destroy();
      pieRef.current = null;

      container.innerHTML = "";

      if (dispatchTimerRef.current !== null) {
        clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }
    };
  }, [canvasSize]);

  // const handleReset = () => setCanvasKey(k => k + 1);

  // Legend data (align with color palette and item labels)
  const legend = items.map((label, i) => ({
    label,
    color: defaultColors[i % defaultColors.length],
  }));

  return (
    <div id="piechart-controls" className="interactive chart-col" style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{
          width: canvasSize ?? undefined,
          height: canvasSize ?? undefined,
          margin: "0 auto",
          // position: "relative"
        }}
      />
      {/* Legend: top-right. Change to left by moving right->left */}
      <div
        style={{
          // position: "absolute",
          // top: 8,
          // right: 8,
          background: "rgba(255,255,255,0.9)",
          border: "1px solid rgba(0,0,0,0.1)",
          borderRadius: 6,
          padding: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          width: "95%",
          marginBottom: 10
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: "#333" }}>Keterangan</div>
        <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
          {legend.map((item, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  background: item.color,
                  border: "1px solid rgba(0,0,0,0.1)",
                  flexShrink: 0
                }}
              />
              <div style={{ fontSize: 12, color: "#333", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* {!isExplanation && <Button variant="contained" onClick={handleReset}>Reset</Button>} */}
    </div>
  );
}